/**
 * Recenzje: kontrakt miedzy serwerem a klientem dla linku recenzenta i panelu administratora.
 *
 * Zyje w shared/ z tego samego powodu co ocenaTypy.ts: obie strony patrza na te same wartosci.
 * Wylacznie stale, typy i male czyste funkcje. Zero DOM, zero API Node.
 *
 * O CO CHODZI: administrator tworzy link /r/<token> dla zewnetrznego recenzenta (konsultant
 * metodyki). Token w adresie jest jedyna "autoryzacja". Recenzent widzi wskazane rozmowy z ocena
 * modelu i przy kazdym wskazaniu zostawia werdykt, komentarz i wlasna wersje. Uwagi trafiaja do
 * tabeli ExpertCorrection, a czlowiek przenosi wnioski do methodology/. Opis: docs/recenzje.md.
 *
 * Numery wypowiedzi w tych typach sa ZAWSZE od 1 (shared/numeracja.ts), nigdy indeksy z bazy.
 */

import type { FindingKind } from "./ocenaTypy.js";
import type { KolorWypowiedzi } from "./koloryWypowiedzi.js";

// ---------------------------------------------------------------------------
// Token linku
// ---------------------------------------------------------------------------

/** Prefiks tokenu linku. Inny niz dt_ (token diagnostyczny), zeby redakcja logow ich nie mylila. */
export const PREFIKS_TOKENU = "rv_";

/** Ksztalt tokenu: prefiks plus 32 znaki hex (16 losowych bajtow). */
export const WZORZEC_TOKENU = /^rv_[0-9a-f]{32}$/;

export function czyTokenLinku(wartosc: unknown): wartosc is string {
  return typeof wartosc === "string" && WZORZEC_TOKENU.test(wartosc);
}

/** Sciezka klienta dla tokenu: /r/<token>. Adres pelny sklada klient z window.location.origin. */
export function sciezkaLinku(token: string): string {
  return `/r/${token}`;
}

// ---------------------------------------------------------------------------
// Slownictwo korekt (kolumny ExpertCorrection)
// ---------------------------------------------------------------------------

/** ExpertCorrection.verdict. Te same wartosci co ClientCorrection.verdict i kolumna w pliku Word. */
export const WERDYKTY = ["ZGADZAM_SIE", "CZESCIOWO", "NIE_ZGADZAM_SIE"] as const;
export type Werdykt = (typeof WERDYKTY)[number];

export function czyWerdykt(wartosc: unknown): wartosc is Werdykt {
  return typeof wartosc === "string" && (WERDYKTY as readonly string[]).includes(wartosc);
}

/** Etykiety werdyktow w interfejsie. */
export const ETYKIETY_WERDYKTOW: Record<Werdykt, string> = {
  ZGADZAM_SIE: "Zgadzam się",
  CZESCIOWO: "Częściowo",
  NIE_ZGADZAM_SIE: "Nie zgadzam się",
};

/** ExpertCorrection.targetType. */
export const RODZAJE_CELU_KOREKTY = ["FINDING", "MISSED_FINDING", "RULE_NOTE", "SESSION"] as const;
export type RodzajCeluKorekty = (typeof RODZAJE_CELU_KOREKTY)[number];

/** targetKey dla celow, ktore sa w sesji tylko jedne. */
export const KLUCZ_NOTATKI = "note";
export const KLUCZ_PRZEJRZANEJ = "finished";

/** ExpertCorrection.status: obieg po stronie administratora (poza zakresem pierwszej wersji, ale kolumna jest). */
export const STATUSY_KOREKTY = ["NOWA", "PRZYJETA", "ODRZUCONA", "WPROWADZONA"] as const;
export type StatusKorekty = (typeof STATUSY_KOREKTY)[number];

/** Limity dlugosci pol (w znakach). Jawne, zeby wklejenie ksiazki konczylo sie czytelnym bledem, a nie 500. */
export const MAX_ZNAKOW_ETYKIETY = 120;
export const MAX_ZNAKOW_KOMENTARZA = 4000;
export const MAX_ZNAKOW_WARIANTU = 2000;
export const MAX_ZNAKOW_NOTATKI = 20_000;
/** Najwiecej rozmow pod jednym linkiem. */
export const MAX_ROZMOW_W_LINKU = 50;
/** Najdluzsza waznosc linku w dniach (null = bez terminu). */
export const MAX_DNI_WAZNOSCI = 365;

/**
 * CZESC NATURALNA klucza wskazania modelu. Wskazanie nie ma trwalego id ("Ocen ponownie" doklada nowe
 * przebiegi), wiec korekta wiaze sie z (soczewka, numer wypowiedzi, znacznik, rodzaj): ten sam
 * zestaw pilnuje wykrywania duplikatow w ocenie. Brak numeru wypowiedzi to 0, brak znacznika "-".
 *
 * Sama czesc naturalna NIE identyfikuje tresci, ktora napisal model, wiec klucz w DTO (RecenzjaWskazanie.klucz)
 * i w ExpertCorrection.targetKey to czesc naturalna + "~" + odcisk tresci wskazania (10 znakow sha256), a
 * duplikaty dostaja jeszcze przyrostek "#2". Klucz sklada WYLACZNIE serwer (services/recenzje/wskazania.ts);
 * klient traktuje go jak nieprzezroczysty napis.
 */
export function kluczWskazania(czesci: {
  lensSlug: string;
  numerWypowiedzi: number | null;
  znacznik: string | null;
  rodzaj: FindingKind;
}): string {
  return `${czesci.lensSlug}:${czesci.numerWypowiedzi ?? 0}:${czesci.znacznik ?? "-"}:${czesci.rodzaj}`;
}

// ---------------------------------------------------------------------------
// Widok recenzenta: GET /api/review/:token i GET /api/review/:token/rozmowy/:id
// ---------------------------------------------------------------------------

/** Ile wskazan rozmowy ocenil recenzent i czy oznaczyl ja jako przejrzana. */
export interface PostepRozmowy {
  /** Liczba wskazan modelu w rozmowie (mocne strony i do poprawy). */
  wskazan: number;
  /** Ile z nich ma werdykt recenzenta. Wskazanie bez dotkniecia nie liczy sie (domyslnie zgoda, ale nic nie zapisano). */
  ocenionych: number;
  przejrzana: boolean;
}

export type StanRozmowyRecenzenta = "NIE_ZACZETA" | "W_TOKU" | "PRZEJRZANA";

export interface RecenzjaRozmowaPodsumowanie {
  id: string;
  tytul: string;
  liczbaWypowiedzi: number;
  liczbaMowcow: number;
  postep: PostepRozmowy;
  stan: StanRozmowyRecenzenta;
}

/** GET /api/review/:token */
export interface RecenzjaInfo {
  /** Dla kogo link (label z panelu administratora). */
  etykieta: string;
  rozmowy: RecenzjaRozmowaPodsumowanie[];
}

export interface RecenzjaMowca {
  nazwa: string;
  /** 1-4: rotacyjny tint awatara, jak w SessionPage (data-tint). */
  tint: 1 | 2 | 3 | 4;
}

export interface RecenzjaWypowiedz {
  /** Numer od 1 (numerWypowiedzi). */
  numer: number;
  /** Indeks w tablicy `mowcy`. */
  mowca: number;
  tekst: string;
  /** "1:05" albo null, gdy rozmowa nie ma czasow (maCzasy === false). */
  czas: string | null;
  /** Wynika wylacznie ze zweryfikowanych wskazan (shared/koloryWypowiedzi.ts). */
  kolor: KolorWypowiedzi;
}

export interface RecenzjaWariant {
  tekst: string;
  dlaczegoLepiej: string;
}

/** To, co recenzent zostawil przy wskazaniu. Wszystkie pola moga byc null (np. sam komentarz bez werdyktu). */
export interface KorektaWskazania {
  werdykt: Werdykt | null;
  komentarz: string | null;
  /** Wlasna wersja wypowiedzi ("Wersja recenzenta"). Ma sens przy wskazaniach do poprawy. */
  wariant: string | null;
}

export interface RecenzjaWskazanie {
  /** kluczWskazania(...) + "~" + odcisk tresci (patrz wyzej). Nieprzezroczysty dla klienta, odsyla go w PUT .../wskazania. */
  klucz: string;
  rodzaj: FindingKind;
  /** Tytul parametru (soczewki), np. "Dopasowanie do rozmówcy". */
  parametr: string;
  /** Numer wypowiedzi od 1; null, gdy wskazanie nie ma juz wypowiedzi. */
  numerWypowiedzi: number | null;
  cytat: string | null;
  /** Offsety cytatu w tekscie wypowiedzi (do podkreslenia); null, gdy nie ma ich albo cytat niezweryfikowany. */
  cytatStart: number | null;
  cytatEnd: number | null;
  cytatZweryfikowany: boolean;
  uzasadnienie: string | null;
  /** Tylko mocna strona. */
  jakRozwijac: string | null;
  /** Tylko do poprawy, 1-3 pozycje. */
  warianty: RecenzjaWariant[];
  korekta: KorektaWskazania | null;
}

/**
 * Uwaga recenzenta przy wypowiedzi (ExpertCorrection MISSED_FINDING; nazwa "pominiete" zostala w API i w typach).
 * Klient pokazuje ja pod wypowiedzia o tym numerze, z "Edytuj" i "Usun".
 */
export interface PominieteWskazanie {
  id: string;
  /** Numer wypowiedzi od 1. null tylko w starych uwagach do calej rozmowy (kontrakt je dopuszcza, nowe zawsze maja numer). */
  numerWypowiedzi: number | null;
  /** Tytul parametru albo null. */
  parametr: string | null;
  komentarz: string;
  utworzone: string;
}

/** GET /api/review/:token/rozmowy/:sessionId */
export interface RecenzjaRozmowa {
  id: string;
  tytul: string;
  /** Opis rozmowy podany przy wgraniu (kontekst), jesli jest. */
  opis: string | null;
  maCzasy: boolean;
  mowcy: RecenzjaMowca[];
  wypowiedzi: RecenzjaWypowiedz[];
  wskazania: RecenzjaWskazanie[];
  /** Tytuly parametrow oceny tej rozmowy (do listy "Czego dotyczy" w formularzu uwagi przy wypowiedzi). */
  parametry: string[];
  /** Uwagi recenzenta przy wypowiedziach, w kolejnosci dodawania. */
  pominiete: PominieteWskazanie[];
  /** Notatka "know-how" do tej rozmowy; pusty tekst, gdy nic nie wpisano. */
  notatka: string;
  postep: PostepRozmowy;
}

/** PUT /api/review/:token/rozmowy/:sessionId/wskazania */
export interface ZapisKorektyWskazania {
  klucz: string;
  werdykt: Werdykt | null;
  komentarz: string | null;
  wariant: string | null;
}

/**
 * POST /api/review/:token/rozmowy/:sessionId/pominiete: nowa uwaga przy wypowiedzi (klient wysyla ja przy pierwszym
 * niepustym tekscie, kolejne zmiany to PUT .../pominiete/:id). Odpowiedz: 201 OdpowiedzDodaniaPominietego.
 */
export interface DodajPominiete {
  numerWypowiedzi: number | null;
  parametr: string | null;
  komentarz: string;
}

/**
 * PUT /api/review/:token/rozmowy/:sessionId/pominiete/:id: zmiana tresci i parametru juz zapisanej uwagi do wypowiedzi.
 * Numer wypowiedzi sie NIE zmienia (uwaga zostaje przy swojej wypowiedzi). Odpowiedz: OdpowiedzDodaniaPominietego.
 */
export interface ZapisPominietego {
  parametr: string | null;
  komentarz: string;
}

/** PUT /api/review/:token/rozmowy/:sessionId/notatka */
export interface ZapisNotatki {
  tekst: string;
}

/** PUT /api/review/:token/rozmowy/:sessionId/przejrzana */
export interface ZapisPrzejrzanej {
  przejrzana: boolean;
}

/** Odpowiedz na kazdy zapis (poza POST i PUT pominiete, ktore zwracaja PominieteWskazanie z postepem). */
export interface OdpowiedzZapisu {
  ok: true;
  postep: PostepRozmowy;
}

export interface OdpowiedzDodaniaPominietego {
  pominiete: PominieteWskazanie;
  postep: PostepRozmowy;
}

/**
 * Kody bledow tej czesci API (pole `error` w JSON-ie, status HTTP w nawiasie):
 *   link_nieprawidlowy (404)    nieznany, wygasly albo uniewazniony token. Jeden kod na wszystko,
 *                               zeby po odpowiedzi nie dalo sie zgadywac tokenow.
 *   nie_ma_takiej_rozmowy (404) rozmowy nie ma pod tym linkiem (albo nie istnieje), albo to rozmowa konta
 *                               uzytkownika (shared/wlascicielRozmowy.ts), takze dopisana do linku wprost w bazie.
 *   nie_ma_takiego_wskazania (404) klucza nie ma w aktualnej ocenie (np. po "Ocen ponownie"), albo id uwagi
 *                               (PUT i DELETE .../pominiete/:id) nie nalezy do tego linku i rozmowy.
 *   rozmowa_nieoceniona (409)   rozmowa nie ma gotowej oceny.
 *   bledne_dane (400)           zly ksztalt albo za dlugie pole; `szczegoly` opisuje, ktore.
 */
export const KODY_BLEDOW_RECENZJI = [
  "link_nieprawidlowy",
  "nie_ma_takiej_rozmowy",
  "nie_ma_takiego_wskazania",
  "rozmowa_nieoceniona",
  "bledne_dane",
] as const;
export type KodBleduRecenzji = (typeof KODY_BLEDOW_RECENZJI)[number];

// ---------------------------------------------------------------------------
// Panel administratora: /api/recenzje/*
// ---------------------------------------------------------------------------

export type StatusLinku = "AKTYWNY" | "WYGASL" | "UNIEWAZNIONY";

export interface LinkRecenzjiAdmin {
  id: string;
  etykieta: string;
  token: string;
  /** "/r/<token>"; pelny adres sklada klient. */
  sciezka: string;
  /** Rozmowy pod linkiem, ktore jeszcze istnieja (id i tytul). */
  rozmowy: { id: string; tytul: string }[];
  /** ISO albo null (bez terminu). */
  waznyDo: string | null;
  uniewazniony: string | null;
  /** Ostatnie otwarcie linku przez recenzenta (ISO) albo null. */
  ostatnieOtwarcie: string | null;
  utworzony: string;
  status: StatusLinku;
  /** Sumy po wszystkich rozmowach linku. */
  postep: { rozmow: number; przejrzanych: number; wskazan: number; ocenionych: number };
}

/**
 * Odmowa POST /api/recenzje/linki (400), gdy wsrod wybranych rozmow jest rozmowa konta uzytkownika: `error` to
 * KOD_ROZMOWY_KONTA_UZYTKOWNIKA, `komunikat` mowi po polsku dlaczego, a pole POLE_ROZMOW_KONT_UZYTKOWNIKOW niesie id
 * takich rozmow w kolejnosci z zadania. Nic sie wtedy nie zapisuje (docs/recenzje.md).
 */
export const KOD_ROZMOWY_KONTA_UZYTKOWNIKA = "rozmowa_konta_uzytkownika";
export const POLE_ROZMOW_KONT_UZYTKOWNIKOW = "rozmowyKontUzytkownikow";

/** POST /api/recenzje/linki */
export interface UtworzLinkWejscie {
  etykieta: string;
  rozmowaIds: string[];
  /** Liczba dni waznosci albo null (bez terminu). */
  waznyDni: number | null;
}

export interface PoprawkaAdmin {
  id: string;
  rozmowaId: string;
  rozmowaTytul: string;
  linkId: string | null;
  linkEtykieta: string | null;
  rodzaj: RodzajCeluKorekty;
  /** Numer od 1 albo null. */
  numerWypowiedzi: number | null;
  /** Tytul parametru (soczewki), jesli znany; w przeciwnym razie slug. */
  parametr: string | null;
  znacznik: string | null;
  werdykt: Werdykt | null;
  komentarz: string | null;
  wariant: string | null;
  /** Kopia wskazania z chwili korekty (rodzaj, cytat, uzasadnienie, warianty). */
  migawka: unknown;
  wersjaMetodyki: string | null;
  status: StatusKorekty;
  utworzona: string;
  zmieniona: string;
}

/** Filtry GET /api/recenzje/poprawki oraz poprawki.csv (parametry zapytania). */
export interface FiltrPoprawek {
  rozmowa?: string;
  link?: string;
  werdykt?: Werdykt;
}
